// MedusaStoreAdapter — the LIVE reference adapter (DECISION-035v2 §1).
//
// Browser → Medusa Store API (publishable-key auth; browser-safe by design —
// the key scopes reads/cart ops to one sales channel; admin ops never touch
// this adapter, they're GCG's server-side half per the ASK-055 ownership call).
//
// Grounded against the live instance 2026-06-11 (probe: ZAR region
// reg_01KTVFK6…, provider pp_paystack_paystack via /store/payment-providers).
// Medusa v2 notes baked in:
//   - auth header is `x-publishable-api-key`
//   - v2 amounts are DECIMAL currency units; the port speaks integer minor
//     units (PortMoney) → convert with round(x * 100) at the boundary.
//   - checkout = payment-collection + payment-session, then cart complete.
//
// The head NEVER imports this directly — it reaches commerce through
// CartService/PortCartTransport and the port types (port.ts). Swap-the-adapter
// stays a config change. AAA precedent: Unreal Online Subsystem.

function toMinor(amount, currencyCode) {
	return { amount: Math.round((Number(amount) || 0) * 100), currencyCode: (currencyCode || "ZAR").toUpperCase() };
}

export class MedusaStoreAdapter {
	backendId = "medusa";

	/**
	 * @param {object} cfg { baseUrl, publishableKey, shopId, regionId?, salesChannelId?, sellerOfRecord? }
	 */
	constructor(cfg) {
		this.cfg = cfg;
		this.baseUrl = cfg.baseUrl.replace(/\/$/, "");
		this._regionId = cfg.regionId || null;   // lazily resolved from /store/regions when absent

		this.catalog = {
			getProduct: (handleOrId) => this._getProduct(handleOrId),
			listProducts: (q) => this._listProducts(q),
			getVariant: async (variantId) => {
				// Store API has no variant-by-id; resolve via its product when needed.
				const { items } = await this._listProducts({ limit: 100 });
				for (const p of items) {
					const v = p.variants.find((x) => x.variantId === variantId);
					if (v) return v;
				}
				return null;
			},
		};

		this.cart = {
			createCart: (shopId) => this._createCart(shopId),
			getCart: (cartId) => this._getCart(cartId),
			addLine: (cartId, variantId, quantity) => this._cartOp(`/store/carts/${cartId}/line-items`, "POST", { variant_id: variantId, quantity }),
			updateLine: (cartId, lineId, quantity) => this._cartOp(`/store/carts/${cartId}/line-items/${lineId}`, "POST", { quantity }),
			removeLine: (cartId, lineId) => this._cartOp(`/store/carts/${cartId}/line-items/${lineId}`, "DELETE", null, cartId),
		};

		this.checkout = {
			getPaymentProviders: (shopId, _region) => this._getPaymentProviders(),
			createCheckoutSession: (cartId, providerId, opts) => this._createCheckoutSession(cartId, providerId, opts),
			completeCheckout: (sessionId) => this._completeCart(sessionId),   // sessionId = cartId for Medusa
		};

		this.order = {
			// Store-side order retrieval needs the customer auth context Medusa
			// scopes orders to; the canonical PortOrder source is GCG's webhook
			// half (ASK-055 ownership split). Best-effort here.
			getOrder: async (_orderId) => null,
			listOrders: async () => ({ items: [], nextCursor: null }),
		};
	}

	// ── plumbing ──────────────────────────────────────────────────────────
	async _fetch(path, method = "GET", body = null) {
		const resp = await fetch(`${this.baseUrl}${path}`, {
			method,
			headers: {
				"Content-Type": "application/json",
				"x-publishable-api-key": this.cfg.publishableKey,
			},
			...(body ? { body: JSON.stringify(body) } : {}),
		});
		const json = await resp.json().catch(() => ({}));
		if (!resp.ok) {
			const err = new Error(`medusa ${method} ${path} → ${resp.status}: ${(json?.message || JSON.stringify(json)).slice(0, 160)}`);
			err.status = resp.status;
			throw err;
		}
		return json;
	}

	async _region() {
		if (this._regionId) return this._regionId;
		const { regions } = await this._fetch("/store/regions");
		const za = regions.find((r) => (r.countries || []).some((c) => c.iso_2 === "za")) || regions[0];
		if (!za) throw new Error("medusa: no regions configured");
		this._regionId = za.id;
		return this._regionId;
	}

	// ── catalog mapping ───────────────────────────────────────────────────
	_mapVariant(v, currency) {
		const cp = v.calculated_price;
		return {
			variantId: v.id,
			sku: v.sku || v.id,
			title: v.title || "",
			price: toMinor(cp?.calculated_amount ?? 0, cp?.currency_code || currency),
			inStock: v.inventory_quantity == null ? true : v.inventory_quantity > 0,
			kapowxProductType: v.metadata?.kapowxProductType || undefined,
		};
	}

	_mapProduct(p, currency) {
		return {
			productId: p.id,
			handle: p.handle,
			title: p.title,
			description: p.description || undefined,
			images: (p.images || []).map((i) => ({ url: i.url })),
			variants: (p.variants || []).map((v) => this._mapVariant(v, currency)),
		};
	}

	async _listProducts(q = {}) {
		const regionId = await this._region();
		const params = new URLSearchParams({ region_id: regionId, limit: String(q.limit ?? 50), ...(q.cursor ? { offset: q.cursor } : {}) });
		const d = await this._fetch(`/store/products?${params}`);
		const items = (d.products || []).map((p) => this._mapProduct(p));
		const next = d.count > (Number(q.cursor) || 0) + items.length ? String((Number(q.cursor) || 0) + items.length) : null;
		return { items, nextCursor: next };
	}

	async _getProduct(handleOrId) {
		const regionId = await this._region();
		if (/^prod_/.test(handleOrId)) {
			const d = await this._fetch(`/store/products/${handleOrId}?region_id=${regionId}`);
			return d.product ? this._mapProduct(d.product) : null;
		}
		const d = await this._fetch(`/store/products?handle=${encodeURIComponent(handleOrId)}&region_id=${regionId}`);
		return d.products?.[0] ? this._mapProduct(d.products[0]) : null;
	}

	// ── cart mapping ──────────────────────────────────────────────────────
	_mapCart(c) {
		const currency = c.currency_code;
		return {
			cartId: c.id,
			shopId: this.cfg.shopId,
			lines: (c.items || []).map((l) => ({
				lineId: l.id,
				variantId: l.variant_id,
				sku: l.variant_sku || l.variant_id,
				title: l.product_title
					? `${l.product_title}${l.title && l.title !== "Default variant" && l.title !== l.product_title ? ` — ${l.title}` : ""}`
					: l.title,
				quantity: l.quantity,
				unitPrice: toMinor(l.unit_price, currency),
				lineTotal: toMinor(l.total ?? l.unit_price * l.quantity, currency),
				image: l.thumbnail ? { url: l.thumbnail } : undefined,
			})),
			subtotal: toMinor(c.item_subtotal ?? c.subtotal ?? 0, currency),
			itemCount: (c.items || []).reduce((n, l) => n + l.quantity, 0),
			supportsCheckout: true,
		};
	}

	async _createCart(_shopId) {
		const regionId = await this._region();
		const body = { region_id: regionId, ...(this.cfg.salesChannelId ? { sales_channel_id: this.cfg.salesChannelId } : {}) };
		const d = await this._fetch("/store/carts", "POST", body);
		return this._mapCart(d.cart);
	}

	async _getCart(cartId) {
		try {
			const d = await this._fetch(`/store/carts/${cartId}`);
			return d.cart ? this._mapCart(d.cart) : null;
		} catch (err) {
			if (err.status === 404) return null;
			throw err;
		}
	}

	async _cartOp(path, method, body, cartIdForDelete = null) {
		const d = await this._fetch(path, method, body);
		// DELETE line-item returns { parent: cart } in v2; POSTs return { cart }.
		const cart = d.cart || d.parent;
		if (cart) return this._mapCart(cart);
		// Fallback: refetch.
		const cartId = cartIdForDelete || String(path.split("/")[3] || "");
		return this._getCart(cartId);
	}

	// ── checkout mapping ──────────────────────────────────────────────────
	async _getPaymentProviders() {
		const regionId = await this._region();
		const d = await this._fetch(`/store/payment-providers?region_id=${regionId}`);
		return (d.payment_providers || []).filter((p) => p.is_enabled !== false).map((p) => ({
			providerId: /paystack/i.test(p.id) ? "paystack" : p.id,
			displayName: /paystack/i.test(p.id) ? "Paystack" : p.id,
			regions: ["ZA"],
			medusaProviderId: p.id,    // the raw id the session call needs
		}));
	}

	async _createCheckoutSession(cartId, providerId, opts = {}) {
		// Paystack requires the customer email at session-init (verified live
		// 2026-06-12: the endpoint rejects a `context` field; the plugin reads
		// `data.email`). Also set it on the cart so the order carries it.
		const email = opts.email || this.cfg.defaultCustomerEmail;
		if (!email) throw new Error("customer email required before checkout (pass opts.email)");
		const raw = await this._fetch(`/store/carts/${cartId}`);
		if (!raw.cart?.email) await this._fetch(`/store/carts/${cartId}`, "POST", { email });
		const providers = await this._getPaymentProviders();
		const provider = providers.find((p) => p.providerId === providerId) || providers[0];
		if (!provider) throw new Error("medusa: no enabled payment provider");

		// v2 checkout: payment-collection on the cart, then a session for the provider.
		const pc = await this._fetch("/store/payment-collections", "POST", { cart_id: cartId });
		const pcId = pc.payment_collection?.id;
		const ps = await this._fetch(`/store/payment-collections/${pcId}/payment-sessions`, "POST", { provider_id: provider.medusaProviderId, data: { email } });
		const session = (ps.payment_collection?.payment_sessions || []).find((s) => s.provider_id === provider.medusaProviderId) || {};
		const data = session.data || {};

		// medusa-payment-paystack surfaces the initialize result as
		// paystackTxAuthorizationUrl / paystackTxAccessCode / paystackTxRef
		// (verified live 2026-06-12) → redirect mode to checkout.paystack.com.
		const redirectUrl = data.paystackTxAuthorizationUrl || data.authorization_url || null;
		return {
			sessionId: cartId,           // Medusa completes on the CART, not the session
			cartId,
			mode: redirectUrl ? "redirect" : "embedded",
			...(redirectUrl ? { redirectUrl } : {}),
			...(data.paystackTxAccessCode || data.access_code ? { clientSecret: data.paystackTxAccessCode || data.access_code } : {}),
			provider: { providerId: provider.providerId, displayName: provider.displayName, regions: provider.regions },
			sellerOfRecord: this.cfg.sellerOfRecord || { storeName: "Kapow", jurisdiction: "ZA" },
		};
	}

	async _completeCart(cartId) {
		const d = await this._fetch(`/store/carts/${cartId}/complete`, "POST");
		if (d.type === "order" && d.order) {
			const o = d.order;
			const currency = o.currency_code;
			return {
				orderId: o.id,
				shopId: this.cfg.shopId,
				status: o.status === "completed" ? "fulfilled" : (o.payment_status === "captured" ? "paid" : "pending"),
				lines: (o.items || []).map((l) => ({
					lineId: l.id, variantId: l.variant_id, sku: l.variant_sku || l.variant_id,
					title: l.title, quantity: l.quantity,
					unitPrice: toMinor(l.unit_price, currency), lineTotal: toMinor(l.total, currency),
				})),
				total: toMinor(o.total, currency),
				placedAt: o.created_at,
				sellerOfRecord: this.cfg.sellerOfRecord || { storeName: "Kapow", jurisdiction: "ZA" },
			};
		}
		throw new Error(`medusa: cart completion did not produce an order (${d.type || "unknown"})`);
	}
}
